/**
 * CropGroupBioDrawer — редактор БИОЛОГИЧЕСКИХ атрибутов биологической культуры
 * (`reference.crop_groups`, миграция 111 / ADR-044 §4).
 *
 * Зачем отдельный drawer: inline-строка мини-справочника тянет только
 * code + название. Биология («Пшеница» → Poaceae / Triticum / шкала BBCH / pH)
 * — это то, что ТОВАРНЫЕ культуры («Пшеница озимая», «Пшеница яровая»)
 * наследуют, и вбивать её по разу на каждую товарную больше не нужно
 * (просьба founder 2026-07-28).
 *
 * Здесь СОЗНАТЕЛЬНО нет вегетационного периода, морозостойкости, выноса NPK,
 * норм высева и сроков сева: у озимой и яровой формы они реально разные и
 * остаются на карточке товарной культуры.
 */
import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  EditorSection,
  ReferenceCatalogDrawer,
} from '@/components/reference/ReferenceCatalogDrawer';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import type { CropGroup, CropGroupCreatePayload } from './types';

/** Radix Select запрещает пустой value → sentinel для «не задано». */
const NONE_VALUE = '__none__';

export const PHENOPHASE_SCALE_OPTIONS = [
  { value: 'BBCH', label: 'BBCH' },
  { value: 'Zadoks', label: 'Zadoks' },
  { value: 'other', label: 'Другое' },
] as const;

interface BioForm {
  latin_name: string;
  botanical_family: string;
  botanical_genus: string;
  botanical_class: string;
  phenophase_scale: string;
  soil_ph_min: string;
  soil_ph_max: string;
}

function toForm(g: CropGroup): BioForm {
  const num = (v: number | null | undefined) => (v == null ? '' : String(v));
  return {
    latin_name: g.latin_name ?? '',
    botanical_family: g.botanical_family ?? '',
    botanical_genus: g.botanical_genus ?? '',
    botanical_class: g.botanical_class ?? '',
    phenophase_scale: g.phenophase_scale ?? '',
    soil_ph_min: num(g.soil_ph_min),
    soil_ph_max: num(g.soil_ph_max),
  };
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);
const numOrNull = (v: string) => (v.trim() ? Number(v) : null);

/** pH: либо оба пусты, либо min ≤ max (backend дублирует проверку 400). */
export function phRangeError(min: string, max: string): string | null {
  if (!min.trim() || !max.trim()) return null;
  const lo = Number(min);
  const hi = Number(max);
  if (Number.isNaN(lo) || Number.isNaN(hi)) return 'pH должен быть числом';
  return lo > hi ? 'pH min не может быть больше pH max' : null;
}

export function buildBioPayload(group: CropGroup, form: BioForm): CropGroupCreatePayload {
  // PATCH заменяет запись целиком → тащим и «нередактируемые здесь» поля,
  // иначе сейв биологии обнулил бы код/категорию/цвет.
  return {
    code: group.code,
    name_translations: group.name_translations,
    category_id: group.category_id,
    color: group.color,
    sort_order: group.sort_order,
    is_active: group.is_active,
    latin_name: orNull(form.latin_name),
    botanical_family: orNull(form.botanical_family),
    botanical_genus: orNull(form.botanical_genus),
    botanical_class: orNull(form.botanical_class),
    phenophase_scale: orNull(form.phenophase_scale),
    soil_ph_min: numOrNull(form.soil_ph_min),
    soil_ph_max: numOrNull(form.soil_ph_max),
  };
}

export interface CropGroupBioDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: CropGroup | null;
  saving?: boolean;
  onSave: (payload: CropGroupCreatePayload) => Promise<void>;
}

export function CropGroupBioDrawer({
  open,
  onOpenChange,
  group,
  saving,
  onSave,
}: CropGroupBioDrawerProps) {
  const [form, setForm] = useState<BioForm | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    setForm(group ? toForm(group) : null);
    setShowErrors(false);
  }, [group]);

  if (!group || !form) return null;

  const set = <K extends keyof BioForm>(k: K, v: BioForm[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const phError = phRangeError(form.soil_ph_min, form.soil_ph_max);
  const groupName = group.name_translations.ru ?? group.code;

  const handleSave = async () => {
    if (phError) {
      setShowErrors(true);
      return;
    }
    try {
      await onSave(buildBioPayload(group, form));
      onOpenChange(false);
    } catch (err) {
      // AK-574 — отказ показываем, drawer оставляем открытым.
      toast.error(describeApiError(err));
    }
  };

  return (
    <ReferenceCatalogDrawer
      open={open}
      onOpenChange={onOpenChange}
      mode="edit"
      entityLabel="биологической культуры"
      headerSlot={
        <span className="text-sm text-foreground">
          {groupName}
          {group.usage_count > 0 && (
            <span className="text-muted-foreground">
              {' '}
              · наследуют {group.usage_count} товарных
            </span>
          )}
        </span>
      }
      saveDisabled={phError !== null}
      saveLoading={saving}
      onSave={handleSave}
    >
      <EditorSection number={1} title="Биология">
        <div className="mb-3">
          <Label htmlFor="grp-latin">Латинское название</Label>
          <Input
            id="grp-latin"
            value={form.latin_name}
            onChange={(e) => set('latin_name', e.target.value)}
            placeholder="Triticum"
            className="mt-1"
          />
        </div>
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="grp-family">Семейство</Label>
            <Input
              id="grp-family"
              value={form.botanical_family}
              onChange={(e) => set('botanical_family', e.target.value)}
              placeholder="Poaceae"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="grp-genus">Род</Label>
            <Input
              id="grp-genus"
              value={form.botanical_genus}
              onChange={(e) => set('botanical_genus', e.target.value)}
              placeholder="Triticum"
              className="mt-1"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="grp-class">Класс</Label>
          <Input
            id="grp-class"
            value={form.botanical_class}
            onChange={(e) => set('botanical_class', e.target.value)}
            placeholder="Liliopsida"
            className="mt-1"
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Эти значения подставляются товарным культурам, у которых своё поле
          пустое. Заполненное на товарной культуре всегда важнее.
        </p>
      </EditorSection>

      <EditorSection number={2} title="Фенофазы и почва">
        <div className="mb-3">
          <Label>Шкала фенофаз</Label>
          <Select
            value={form.phenophase_scale || NONE_VALUE}
            onValueChange={(v) => set('phenophase_scale', v === NONE_VALUE ? '' : v)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>— не задана</SelectItem>
              {PHENOPHASE_SCALE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">
            Сами стадии заводятся один раз на карточке любой товарной культуры
            этой биологической — остальные видят их как унаследованные.
          </p>
        </div>
        <Label className="mb-2 block">Кислотность почвы (pH)</Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Input
              type="number"
              step="0.1"
              value={form.soil_ph_min}
              onChange={(e) => set('soil_ph_min', e.target.value)}
              placeholder="мин"
              className={cn(showErrors && phError && 'border-destructive')}
            />
            <p className="mt-1 text-xs text-muted-foreground">минимум</p>
          </div>
          <div>
            <Input
              type="number"
              step="0.1"
              value={form.soil_ph_max}
              onChange={(e) => set('soil_ph_max', e.target.value)}
              placeholder="макс"
              className={cn(showErrors && phError && 'border-destructive')}
            />
            <p className="mt-1 text-xs text-muted-foreground">максимум</p>
          </div>
        </div>
        {showErrors && phError && (
          <div className="mt-2 flex items-center gap-2 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5" />
            {phError}
          </div>
        )}
      </EditorSection>
    </ReferenceCatalogDrawer>
  );
}

export default CropGroupBioDrawer;
